import { AlertTriangle, Edit, Menu, RotateCcw, Save, Users, X, Zap } from 'lucide-react';
import { getProfile } from '../../config/activityProfileConfig';
import useGrandeEcole from '../../stores/useGrandeEcole';

export default function AppHeader({ 
  sidebarCollapsed, 
  onToggleSidebar, 
  activeSection,
  isEditing = false,
  onToggleEdit,
  onSave,
  onCancel,
  onNavigate
}) {
  const {
    activityProfile,
    activeLearners,
    totalLearners,
    totalPesasCirculating,
    criticalAlerts,
    user,
    logout
  } = useGrandeEcole();

  const currentProfile = getProfile(activityProfile);

  return (
    <header className="sticky top-0 z-50 border-b border-orange-900/40 bg-zinc-900/90 backdrop-blur-md">
      <div className="h-11 flex items-center px-4 text-xs uppercase tracking-widest">
        {/* GAUCHE */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button
            onClick={onToggleSidebar}
            className="p-1.5 hover:bg-orange-950/40 rounded transition-colors shrink-0 hidden md:flex"
            aria-label={sidebarCollapsed ? 'Ouvrir menu' : 'Fermer menu'}
          >
            {sidebarCollapsed ? <Menu size={14} /> : <X size={14} />}
          </button>

          <div className="font-black tracking-tighter text-orange-300/90 text-sm md:text-base truncate">
            <span className="hidden sm:inline">{currentProfile.label.toUpperCase()} </span>
            <span className="sm:hidden">{currentProfile.icon} </span>
            <span className="text-orange-500/70">CONTRÔLE</span>
          </div>

          <div className="h-3.5 w-px bg-orange-900/60 mx-1 shrink-0 hidden md:block" />

          <div className="hidden md:flex items-center gap-5 opacity-75">
            <span className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="hidden lg:inline">LIAISON CŒUR ACTIVE</span>
              <span className="hidden md:inline lg:hidden text-fuchsia-400/70 text-[9px]">
                {currentProfile.icon} {currentProfile.label.toUpperCase()}
              </span>
            </span>
            <span>
              {new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}{' '}
              {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} UTC+1
            </span>
          </div>
        </div>

        {/* DROITE */}
        <div className="ml-auto flex items-center gap-2 md:gap-4 lg:gap-6 opacity-70 text-[10px] md:text-xs shrink-0">
          <span className="hidden lg:inline">BUILD 2024.11.22</span>
          <span className="text-emerald-400 hidden md:inline">MOTEUR v1.0 – STABLE</span>

          <div className="hidden xl:flex items-center gap-4">
            <span className="flex items-center gap-1">
              <Users size={10} />
              {activeLearners}/{totalLearners}
            </span>
            <span className="flex items-center gap-1">
              <Zap size={10} className="text-amber-400" />
              {(totalPesasCirculating / 1000000).toFixed(2)}M₧
            </span>
            {criticalAlerts > 0 && (
              <span className="flex items-center gap-1 text-red-400">
                <AlertTriangle size={10} />
                {criticalAlerts}
              </span>
            )}
          </div>

          {criticalAlerts > 0 && (
            <span className="flex xl:hidden items-center gap-1 text-red-400">
              <AlertTriangle size={10} />
              {criticalAlerts}
            </span>
          )}

          {/* Mode Lab (facultatif, à connecter si besoin) */}
          {['parcours', 'eleves', 'cohortes', 'personnel', 'branding'].includes(activeSection) && !isEditing && onToggleEdit && (
            <button
              onClick={onToggleEdit}
              className="flex items-center gap-1.5 px-2.5 md:px-4 py-1.5 text-xs font-medium rounded border transition-all bg-black/40 border-orange-800/50 text-orange-400 hover:bg-orange-950/40"
            >
              <Edit size={13} />
              <span className="hidden sm:inline">Mode Lab</span>
            </button>
          )}

          {isEditing && (
            <div className="flex items-center gap-1.5 md:gap-2 bg-orange-950/40 px-2 md:px-3 py-1 rounded border border-orange-800/50">
              <button
                onClick={onToggleEdit}
                className="flex items-center gap-1 text-orange-400 hover:text-orange-300 transition-colors"
                title="Quitter Mode Lab"
              >
                <RotateCcw size={13} />
                <span className="hidden sm:inline text-[10px]">Quitter</span>
              </button>
              <div className="w-px h-3.5 bg-orange-800/60" />
              <span className="text-orange-300 font-medium hidden md:inline text-[10px]">MODE LAB</span>
              <button
                onClick={onSave}
                className="p-1.5 bg-emerald-900/40 hover:bg-emerald-800/60 rounded text-emerald-300 transition-colors"
                title="Sauvegarder"
              >
                <Save size={13} />
              </button>
              <button
                onClick={onCancel}
                className="p-1.5 bg-red-900/40 hover:bg-red-800/60 rounded text-red-300 transition-colors"
                title="Annuler"
              >
                <X size={13} />
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}